import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useGoalCatalogueQuery } from "../api";
import { nestCatalogue } from "../lib/nest";

import { GoalPicker } from "./GoalPicker";

/**
 * Create / edit form for a training group.
 *
 * On create, `initialGoalIds` is the default demonstration set — the SPA's
 * counterpart of the Django form arriving prefilled. It is seeded into local
 * state, so the administrator's own edits survive; the server takes exactly
 * what is submitted.
 *
 * The name and the floor of one goal are checked here for an immediate message,
 * but the server is the authority: both rules are enforced again by the
 * serializer, which is what the Django app and any other caller meet.
 */
export function FormationGroupForm({
  initialName = "",
  initialGoalIds,
  submitLabel,
  submitting,
  serverErrors,
  onSubmit,
  onCancel,
}: {
  initialName?: string;
  initialGoalIds: string[];
  submitLabel: string;
  submitting?: boolean;
  serverErrors?: Record<string, string>;
  onSubmit: (values: { name: string; goals: string[] }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const catalogue = useGoalCatalogueQuery();

  const [name, setName] = useState(initialName);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialGoalIds),
  );
  const [touched, setTouched] = useState(false);

  const tree = useMemo(() => {
    if (!catalogue.data) return [];
    return nestCatalogue(
      catalogue.data.thematics,
      catalogue.data.issues,
      catalogue.data.goals,
    );
  }, [catalogue.data]);

  const nameError =
    (touched && !name.trim() && t("formationGroups.form.nameRequired")) ||
    serverErrors?.name;
  const goalsError =
    (touched && selected.size === 0 && t("formationGroups.form.goalsRequired")) ||
    serverErrors?.goals;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!name.trim() || selected.size === 0) return;
    onSubmit({ name: name.trim(), goals: [...selected] });
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card>
        <CardContent className="space-y-2 pt-6">
          <Label htmlFor="formation-group-name" className="block">
            {t("formationGroups.form.nameLabel")}
          </Label>
          <Input
            id="formation-group-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("formationGroups.form.namePlaceholder")}
            aria-invalid={Boolean(nameError)}
          />
          {nameError ? (
            <p role="alert" className="text-sm text-destructive">
              {nameError}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <GoalPicker
        tree={tree}
        selected={selected}
        onChange={setSelected}
        initialGoalIds={initialGoalIds}
        error={goalsError || undefined}
      />

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("formationGroups.cancel")}
        </Button>
      </div>
    </form>
  );
}
